import { Pipe, PipeTransform } from '@angular/core';

/** The properties an object may carry its identity in, tried in this order. */
const idProperties = ['id', 'code', 'value'] as const;

@Pipe({
  name: 'attributeFromObjectIdPipe',
})
export class AttributeFromObjectIdPipe implements PipeTransform {
  /**
   * Looks an object up in `objects` by its id and returns one of its attributes.
   *
   * The lists this runs over come from different sources and do not agree on one identity
   * property, so `id`, `code` and `value` are tried in turn — but only until one of them
   * **matches**. The match is the answer: an object found by its `id` that carries no usable
   * attribute returns a blank rather than sending the search on to `code`, which would resolve
   * the same id against a different object and name the wrong thing.
   *
   * Anything unresolved returns an empty string, so a template renders a blank rather than
   * «undefined».
   *
   * @param {readonly unknown[] | null | undefined} objects - The list to look the id up in
   * @param {string} attributeName - The attribute of the matched object to return
   * @param {string | number | null | undefined} objectId - The id to look for
   * @returns {string | number} The attribute, or an empty string when nothing resolves
   */
  public transform(
    objects: readonly unknown[] | null | undefined,
    attributeName: string,
    objectId: string | number | null | undefined,
  ): string | number {
    if (!objects || !attributeName || objectId === null || objectId === undefined || objectId === '') {
      return '';
    }

    for (const idProperty of idProperties) {
      const match = objects.find(
        (item): item is Record<string, unknown> =>
          !!item && typeof item === 'object' && (item as Record<string, unknown>)[idProperty] === objectId,
      );

      if (!match) {
        continue;
      }

      const value = match[attributeName];

      return typeof value === 'string' || typeof value === 'number' ? value : '';
    }

    return '';
  }
}
